'use client'

import * as React from 'react'
import { ResizableContainer } from '~/sotano-kit/primitives/resizable-container'
import { ChangesTree } from '../../changes/components/sidebar/changes-tree'
import { useDiff } from '~/lib/diff/use-diff'
import { useCommit } from '~/hooks-data/use-commit'
import { Commit, isCommit } from '@basehub/data/replicache/index'
import { BlockDiff } from '~/app/(dash)/[team-slug]/(repo)/[repo-slug]/components/diff/block-diff'
import { ScrollArea } from '~/sotano-kit/primitives/scroll-area'
import { CommitEmptyState } from './empty-state'
import { useCommitBlockTreeManager } from '~/hooks-data/blocks-tree'
import {
  useSelectedBranchOrCommitQuery,
  useSelectedBranch,
} from '~/lib/commits/use-selected-branch-or-commit'
import { parseTab, useTabsStore } from '../../explore/components/tabs/store'
import { Skeleton } from '~/sotano-kit/primitives/skeleton'
import { run } from '@basehub/utils'
import { BreadcrumbStickyContainer } from '../../explore/components/tabs/dashboard-breadcrumb'
import { Breadcrumb, BreadcrumbItem } from '~/sotano-kit/primitives/breadcrumb'
import {
  CounterClockwiseClockIcon,
  DotsVerticalIcon,
  ResetIcon,
} from '@radix-ui/react-icons'
import {
  Dialog,
  DialogClose,
  DialogFooter,
  DialogHeader,
  DialogTrigger,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from '~/sotano-kit/primitives/dialog'
import { Button } from '~/sotano-kit/primitives/button'
import { useRepoReplicache } from '~/hooks-data/use-repo-replicache'
import { toast } from 'sonner'
import { useMutation } from '@tanstack/react-query'
import { useToggleState } from '~/hooks/use-toggle-state'
import { RestoreToHeadCommitIcon } from '~/components/icons/restore-to-head-commit'
import { tabContentWrapperId } from '../../tab-content/wrapper'
import { $breadcrumbCta } from '../../explore/components/tabs/dashboard-breadcrumb/preview-button'
import { EditorModeProvider } from '../../explore/components/editor-mode-provider'
import { CommitNavigationProvider } from '../_store'
import { getURL } from '@basehub/shared-constants'
import { getClerkSessionFromCookie } from '~/replicache/frontend/get-clerk-session-from-cookie'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '~/sotano-kit/primitives/dropdown-menu'

import { IconButton } from '~/sotano-kit/primitives/icon-button'
import { useIsMemberOrHigher } from '~/hooks-data/use-user-role'
import { CommitDisplay } from '~/components/commit-display'
import { useRepoStore } from '../../explore/components/repo-store'

export const HardContentResetDialog = ({
  children,
  commitId,
}: {
  children?: React.ReactNode
  commitId: string | null
}) => {
  const { branch } = useSelectedBranch()
  const replicacheData = useRepoReplicache().data
  const toggleState = useToggleState()

  const { mutate, isLoading } = useMutation(
    async () => {
      if (!branch || !replicacheData || !commitId) return

      const response = await fetch(
        getURL(
          'pump',
          process.env.NEXT_PUBLIC_ENVIRONMENT as 'production' | undefined
        ) + '/commit/hard-reset',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'x-basehub-session': getClerkSessionFromCookie() || '',
          },
          body: JSON.stringify({
            teamId: replicacheData.team.id,
            repoId: replicacheData.repoId,
            branchId: branch.id,
            commitId: commitId,
          }),
        }
      )
      const json = (await response.json()) as {
        success: boolean
        error?: string
      }
      if (!json.success) {
        throw new Error(json.error)
      }
    },
    {
      onError: (e) => {
        console.error(e)
        toast.error('Something went wrong')
      },
      onSuccess: async () => {
        toast.success('Hard content reset completed successfully')
        toggleState.handleToggle()
        // Pull latest data to reflect the changes
        await replicacheData?.replicache?.pull({ now: true })
      },
    }
  )

  if (!branch || !commitId) {
    return null
  }

  return (
    <Dialog open={toggleState.isOn} onOpenChange={toggleState.handleToggle}>
      <DialogTrigger asChild>
        {children ?? (
          <button
            className={$breadcrumbCta({
              className: 'font-medium',
            })}
            onClick={(e) => e.stopPropagation()}
          >
            <ResetIcon className="shrink-0" />
            Hard content reset
          </button>
        )}
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Hard Content Reset</DialogTitle>
          <DialogDescription>
            This will permanently reset the branch to point to this commit and
            discard all working changes. This action cannot be undone.
            <br />
            <br />
            <strong>Warning:</strong> All uncommitted work will be lost.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter divider>
          <DialogClose asChild>
            <Button intent="secondary">Cancel</Button>
          </DialogClose>
          <Button intent="tertiary" onClick={() => mutate()}>
            {isLoading ? 'Resetting...' : 'Reset Branch'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export const CommitContent = ({ commitId }: { commitId: Commit['id'] }) => {
  const { data: branchOrCommit } = useSelectedBranchOrCommitQuery()

  const fallbackHeadCommitId = React.useMemo(() => {
    if (!branchOrCommit) return undefined
    return isCommit(branchOrCommit)
      ? branchOrCommit.id
      : (branchOrCommit.headCommitId ?? false)
  }, [branchOrCommit])

  if (fallbackHeadCommitId === undefined) return <></> // loading
  if (fallbackHeadCommitId === false) return <CommitEmptyState />

  return (
    <EditorModeProvider value="read">
      <ActualContent commitId={commitId ?? fallbackHeadCommitId} />
    </EditorModeProvider>
  )
}

const ActualContent = ({ commitId }: { commitId: Commit['id'] }) => {
  const { data: selectedCommitQueryData } = useCommit(commitId)
  const selectedCommit = selectedCommitQueryData?.result

  const { data: parentCommitQueryData, isLoading: isLoadingParentCommit } =
    useCommit(
      selectedCommit?.parentCommitId,
      selectedCommitQueryData?.isFallback
    )
  const parentCommit = parentCommitQueryData?.result
  const { data: selectedBranchOrCommit } = useSelectedBranchOrCommitQuery()
  const treeContainerRef = React.useRef<HTMLDivElement>(null)
  const isMemberOrHigher = useIsMemberOrHigher()

  const { data: oldCommitBlockTree } = useCommitBlockTreeManager({
    commitId: isLoadingParentCommit ? undefined : parentCommit?.id,
    rootBlockId: parentCommit?.rootBlockId ?? undefined,
  })
  const { data: newCommitBlockTree } = useCommitBlockTreeManager({
    commitId: selectedCommit?.id,
    rootBlockId: selectedCommit?.rootBlockId ?? undefined,
  })

  const diffResult = useDiff({
    treeOld:
      isLoadingParentCommit || parentCommit
        ? (oldCommitBlockTree as any)
        : false,
    treeNew: newCommitBlockTree as any,
    treeCommonAncestor: false,
  })

  const setFocus = useRepoStore((s) => s.setFocus)
  const addTab = useTabsStore((s) => s.addTab)
  const activeTab = useTabsStore((s) => s.activeTab)
  const selectBlockChangeForCommitTab = useTabsStore(
    (s) => s.selectBlockChangeForCommitTab
  )

  const parsedActiveTab = activeTab ? parseTab(activeTab) : null
  const _activeBlockId =
    parsedActiveTab?.type === 'commit'
      ? (parsedActiveTab.blockChangeId ?? null)
      : null
  const [defaultActiveBlockId, setDefaultActiveBlockId] = React.useState<
    string | null
  >(null)
  const resolveActiveBlockId = _activeBlockId ?? defaultActiveBlockId

  const isCheckingOutCommit =
    selectedBranchOrCommit && isCommit(selectedBranchOrCommit)

  React.useEffect(() => {
    if (_activeBlockId) return
    const treeContainer = treeContainerRef.current
    if (!treeContainer) return

    const activeKey = treeContainer.querySelector<HTMLElement>(
      '[data-tree-list-child-active="true"]'
    )?.dataset.treeListChild
    if (activeKey && typeof activeKey === 'string') {
      setDefaultActiveBlockId(activeKey)
    }
  }, [_activeBlockId, diffResult.diff?.changesTree])

  return (
    <>
      <BreadcrumbStickyContainer>
        <Breadcrumb>
          <BreadcrumbItem active>
            {selectedCommit && (
              <CommitDisplay
                commit={selectedCommit}
                avatarSize="petite"
                truncateMessage={true}
                messageClassName="max-w-[300px]"
              />
            )}
          </BreadcrumbItem>
        </Breadcrumb>
        <div className="flex items-center gap-x-2">
          {!isCheckingOutCommit && parsedActiveTab?.type === 'commit' && (
            <div className="flex items-center gap-x-1">
              <RollbackDialog commitId={parsedActiveTab.commitId} />
              {isMemberOrHigher && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <IconButton
                      className="cursor-default bg-transparent text-dark-faint hover:bg-dark-shade-hover"
                      tabIndex={-1}
                    >
                      <DotsVerticalIcon width={16} height={16} />
                    </IconButton>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent sideOffset={6} align="end">
                    <HardContentResetDialog commitId={parsedActiveTab.commitId}>
                      <DropdownMenuItem
                        className="space-x-2 text-xs leading-4 text-dark-error cursor-pointer"
                        onSelect={(e) => e.preventDefault()}
                      >
                        <ResetIcon />
                        <span>Hard content reset</span>
                      </DropdownMenuItem>
                    </HardContentResetDialog>
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </div>
          )}
        </div>
      </BreadcrumbStickyContainer>
      <div className="flex w-full flex-1 bg-dark-base">
        <ResizableContainer
          minWidth={150}
          maxWidth={400}
          defaultWidth={250}
          direction="right"
          className="flex flex-col w-full max-h-[var(--editor-height)] overflow-auto border-r border-dark-border-solid"
          widthLocalStorageKey="repo-commits-content-sidebar-width"
          noBorderHighlight
        >
          <CommitNavigationProvider
            currentCommit={selectedCommit ?? null}
            previousCommit={parentCommit ?? null}
            activeBlockId={resolveActiveBlockId}
          >
            <ScrollArea viewportClassName="py-3" ref={treeContainerRef}>
              {run(() => {
                if (!diffResult.diff) {
                  return (
                    <div className="space-y-1 px-3">
                      <Skeleton height={24} className="w-full" />
                      <Skeleton height={24} className="w-full" />
                      <Skeleton height={24} className="w-full" />
                      <Skeleton height={24} className="w-full" />
                      <Skeleton height={24} className="w-full" />
                    </div>
                  )
                }

                if (diffResult.diff.changesCount < 1) return null

                return (
                  <ChangesTree
                    className="flex-grow"
                    changesTreeManager={diffResult.diff.changesTree}
                    treeNew={diffResult.treeNew}
                    treeOld={diffResult.treeOld || null}
                    activeKey={
                      _activeBlockId ? { label: '', id: _activeBlockId } : null
                    }
                    autoFocusKey={resolveActiveBlockId}
                    onActiveKeyChange={(key) =>
                      selectBlockChangeForCommitTab({ blockChangeId: key })
                    }
                    onOpenBlock={(blockId) => {
                      addTab({ key: blockId, partiallyOpen: true })
                      setFocus({ type: 'block:sidebar', blockId })
                    }}
                    selectedKeys={new Set()}
                    setSelectedKeys={() => {}}
                    changesCount={diffResult.diff.changesCount}
                    defaultSelectFirst
                  />
                )
              })}
            </ScrollArea>
          </CommitNavigationProvider>
        </ResizableContainer>
        <ScrollArea
          orientation="vertical"
          id={tabContentWrapperId}
          className="max-h-[var(--editor-height)] flex-grow [&>div>div]:h-full"
        >
          {resolveActiveBlockId && parsedActiveTab?.type === 'commit' && (
            <div className="flex flex-col flex-1 w-full px-16 pt-14 relative scale-100 min-h-full">
              <BlockDiff
                blockId={resolveActiveBlockId}
                diffResult={diffResult}
                displayBlockLinks={false}
                diffTabPrefix={`commit:${commitId}:`}
              />

              <div className="h-32" />
            </div>
          )}
        </ScrollArea>
      </div>
    </>
  )
}

export const RollbackDialog = ({
  children,
  commitId,
}: {
  children?: React.ReactNode
  commitId: string | null
}) => {
  const { branch } = useSelectedBranch()
  const replicacheData = useRepoReplicache().data
  const toggleState = useToggleState()

  const [shouldDisableRollbackMode, setShouldDisableRollbackMode] =
    React.useState(false)

  const { mutate, isLoading } = useMutation(
    async () => {
      if (!branch || !replicacheData) return

      const isDisabling = shouldDisableRollbackMode
      const requestPayload = {
        teamId: replicacheData.team.id,
        repoId: replicacheData.repoId,
        branchId: branch.id,
        commitId: isDisabling ? null : commitId,
      }

      console.log('[RollbackDialog] Starting rollback mutation', {
        isDisabling,
        branchId: branch.id,
        branchName: branch.name,
        currentRollback: branch.rollback,
        commitId: isDisabling ? null : commitId,
        requestPayload,
      })

      const response = await fetch(
        getURL(
          'pump',
          process.env.NEXT_PUBLIC_ENVIRONMENT as 'production' | undefined
        ) + '/rollback',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'x-basehub-session': getClerkSessionFromCookie() || '',
          },
          body: JSON.stringify(requestPayload),
        }
      )

      console.log('[RollbackDialog] Received response', {
        status: response.status,
        statusText: response.statusText,
        isDisabling,
      })

      const json = (await response.json()) as {
        success: boolean
        error?: string
      }

      console.log('[RollbackDialog] Parsed response JSON', {
        json,
        isDisabling,
      })

      if (!json.success) {
        console.error('[RollbackDialog] Rollback failed', {
          error: json.error,
          isDisabling,
          branchId: branch.id,
        })
        throw new Error(json.error)
      }

      console.log('[RollbackDialog] Rollback succeeded', {
        isDisabling,
        branchId: branch.id,
      })
    },
    {
      onError: (e) => {
        console.error('[RollbackDialog] Mutation error', e)
        toast.error('Something went wrong')
      },
      onSuccess: () => {
        console.log('[RollbackDialog] Mutation onSuccess callback triggered', {
          shouldDisableRollbackMode,
          branchId: branch?.id,
        })
        toast.success('Rolled back to commit successfully')
        toggleState.handleToggle()
      },
    }
  )

  React.useEffect(() => {
    if (isLoading) return

    let result = false
    if (!branch || !commitId) {
      result = true
    } else if (branch.rollback?.commitId === commitId) {
      result = true
    } else if (branch.headCommitId === commitId) {
      result = true
    }

    setShouldDisableRollbackMode(result)
  }, [branch, commitId, isLoading])

  if (!branch?.rollback?.commitId && branch?.headCommitId === commitId) {
    return null
  }

  return (
    <Dialog open={toggleState.isOn} onOpenChange={toggleState.handleToggle}>
      <DialogTrigger asChild>
        {children ?? (
          <button
            className={$breadcrumbCta({
              className: 'font-medium',
            })}
            onClick={(e) => e.stopPropagation()}
          >
            {shouldDisableRollbackMode ? (
              <RestoreToHeadCommitIcon className="shrink-0" />
            ) : (
              <CounterClockwiseClockIcon className="shrink-0" />
            )}
            {shouldDisableRollbackMode ? 'Disable' : 'Instant'} rollback
          </button>
        )}
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {shouldDisableRollbackMode && 'Disable '}Instant Rollback
          </DialogTitle>
          <DialogDescription>
            {shouldDisableRollbackMode
              ? 'Disabling the rollback will make the GraphQL API target the latest commit again.'
              : `Make the the GraphQL API target this commit. New commits will be ignored until you disable the rollback.`}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter divider>
          <DialogClose asChild>
            <Button intent="secondary">Close</Button>
          </DialogClose>
          <Button onClick={() => mutate()}>
            {isLoading ? 'Loading...' : 'Confirm'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
